// src/pages/calidad/PpniPncPendientes.jsx
// PPNI y PNC sin disposición: Calidad asigna la disposición y quién la autoriza.
import { useEffect, useState } from 'react';
import { FiExternalLink } from 'react-icons/fi';
import api from '../../services/api';
import s from '../mantenimiento/estilos';

const fmtFecha = (d) => (d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fmtKg = (v) => (v === null || v === undefined ? '—' : `${Number(v).toLocaleString('es-MX', { maximumFractionDigits: 3 })} kg`);

export default function PpniPncPendientes() {
  const [filas, setFilas] = useState([]);
  const [disposiciones, setDisposiciones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [editandoId, setEditandoId] = useState(null);
  const [form, setForm] = useState({ disposicion: '', firma_disposicion: '' });
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  async function cargar() {
    setCargando(true);
    try {
      const [p, c] = await Promise.all([api.get('/api/calidad/ppni-pnc/pendientes'), api.get('/api/calidad/ppni-pnc/catalogos')]);
      setFilas(p.data.data);
      setDisposiciones(c.data.data.listas.disposicion.filter((d) => d.activo).map((d) => d.valor));
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'No se pudo cargar lo pendiente.' });
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => { cargar(); }, []);

  async function handleGuardar(f) {
    if (!form.disposicion || !form.firma_disposicion.trim()) {
      setMensaje({ tipo: 'error', texto: 'Elige la disposición y escribe quién la autoriza.' });
      return;
    }
    try {
      setGuardando(true);
      await api.put(`/api/calidad/ppni-pnc/registros/${f.id}/disposicion`, form);
      setMensaje({ tipo: 'ok', texto: `✓ Folio ${f.folio}: ${form.disposicion}.` });
      setEditandoId(null);
      setFilas((prev) => prev.filter((x) => x.id !== f.id));
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo guardar.' });
    } finally {
      setGuardando(false);
    }
  }

  if (cargando) return <div style={s.paginaContenedor}>Cargando...</div>;
  const kgPendientes = filas.reduce((t, f) => t + Number(f.kg_desviacion || 0), 0);

  return (
    <div style={{ ...s.paginaContenedor, maxWidth: 980 }} className="contenedor-captura">
      <h1 style={{ ...s.tituloPagina, marginBottom: 2 }}>PPNI y PNC — Pendientes de disposición</h1>
      <div style={{ fontSize: 12, color: '#8A7690', marginBottom: 14 }}>Registros que todavía no tienen disposición. Cuentan en contra del KPI 5.6 hasta que se les asigne.</div>

      {mensaje && <div style={{ ...s.mensaje, color: mensaje.tipo === 'ok' ? '#3E7C5A' : '#B23A48' }}>{mensaje.texto}</div>}

      {filas.length === 0 ? (
        <div style={{ ...s.tarjeta, textAlign: 'center', color: '#8A7690' }}>Todo al día — sin pendientes de disposición 🎉</div>
      ) : (
        <>
          <div style={s.kgrid}>
            <div style={{ ...s.kcard, borderTop: '3px solid #B23A48' }}>
              <div style={s.kLbl}>Pendientes</div>
              <div style={{ ...s.kVal, fontSize: 30, color: '#B23A48' }}>{filas.length}</div>
              <div style={s.kSub}>{fmtKg(kgPendientes)} sin disposición</div>
            </div>
            <div style={{ ...s.kcard, borderTop: '3px solid #B8862E' }}>
              <div style={s.kLbl}>El más antiguo</div>
              <div style={{ ...s.kVal, fontSize: 30, color: '#B8862E' }}>{filas[0].dias_pendiente} días</div>
              <div style={s.kSub}>Folio {filas[0].folio} · {fmtFecha(filas[0].fecha_deteccion)}</div>
            </div>
          </div>

          <div style={s.tarjeta}>
            {filas.map((f) => (
              <div key={f.id} style={x.fila}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <div>
                    <div style={x.nombre}>
                      <span style={{ color: '#6A2C75' }}>{f.folio}</span> · {f.producto || 'Sin producto'}
                      <span style={{ ...x.tag, ...(f.clasificacion === 'PPNI' ? x.tagPpni : x.tagPnc) }}>{f.clasificacion || '—'}</span>
                    </div>
                    <div style={x.detalle}>
                      {fmtFecha(f.fecha_deteccion)} · {fmtKg(f.kg_desviacion)}{f.pct_muestra !== null && ` (${f.pct_muestra}% de la muestra)`}
                      {f.lote && ` · Lote ${f.lote}`}{f.area_proceso && ` · ${f.area_proceso}`}{f.turno && ` · ${f.turno}`}
                    </div>
                    {f.tipo_desviacion && <div style={x.detalle}>{f.tipo_desviacion}</div>}
                    {f.descripcion && <div style={{ ...x.detalle, fontStyle: 'italic', color: '#3A2E42' }}>{f.descripcion}</div>}
                    {f.imagen_link && <a href={f.imagen_link} target="_blank" rel="noreferrer" style={x.link}><FiExternalLink size={11} /> Ver imagen</a>}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: f.dias_pendiente > 3 ? '#B23A48' : '#8A7690', whiteSpace: 'nowrap' }}>
                    {f.dias_pendiente} día(s)
                  </div>
                </div>

                {editandoId === f.id ? (
                  <div style={x.formInline}>
                    <select value={form.disposicion} onChange={(e) => setForm({ ...form, disposicion: e.target.value })} style={{ ...s.input, flex: '1 1 180px' }}>
                      <option value="">— Disposición —</option>
                      {disposiciones.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <input type="text" placeholder="Autorizó (firma)" value={form.firma_disposicion} onChange={(e) => setForm({ ...form, firma_disposicion: e.target.value })} style={{ ...s.input, flex: '1 1 180px' }} />
                    <button onClick={() => handleGuardar(f)} disabled={guardando} style={s.botonGuardarChico}>{guardando ? '...' : 'Guardar'}</button>
                    <button onClick={() => setEditandoId(null)} style={s.linkChico}>Cancelar</button>
                  </div>
                ) : (
                  <button onClick={() => { setEditandoId(f.id); setForm({ disposicion: '', firma_disposicion: '' }); setMensaje(null); }} style={x.botonAsignar}>
                    Asignar disposición
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const x = {
  fila: { display: 'flex', flexDirection: 'column', gap: 8, paddingBottom: 14, marginBottom: 14, borderBottom: '1px solid #F0E9E0' },
  nombre: { fontSize: 13.5, fontWeight: 700, color: '#2A1830', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  detalle: { fontSize: 11.5, color: '#8A7690', marginTop: 2 },
  tag: { fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6 },
  tagPpni: { color: '#B23A48', background: '#FDF3F1' },
  tagPnc: { color: '#B2662A', background: '#FFF6E9' },
  link: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: '#4A1E5C', fontWeight: 600, marginTop: 4 },
  formInline: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  botonAsignar: { padding: '7px 14px', fontSize: 12, fontWeight: 600, color: '#6E4F80', background: '#EFE9F3', border: '1px solid #A98BB8', borderRadius: 6, cursor: 'pointer', alignSelf: 'flex-start' },
};